<template>
  <div class="brand-overview-view flex flex-col">
    <page-header back="/brands" :title="brand?.name ?? tr('Loading...')">
      <template #buttons>
        <ui-button v-if="isLoading" disabled>
          <pencil-icon />
          {{ tr('Edit') }}
        </ui-button>
        <ui-button v-else as-child>
          <router-link :to="'/brands/' + $route.params.id + '/edit'">
            <pencil-icon />
            {{ tr('Edit') }}
          </router-link>
        </ui-button>

        <ui-dropdown-menu v-if="$root.isInOriginalCompany">
          <ui-dropdown-menu-trigger as-child>
            <ui-button
              variant="outline"
              size="icon"
              class="flex-none!"
              :disabled="isLoading"
              :aria-label="tr('More actions')"
            >
              <ellipsis-icon />
            </ui-button>
          </ui-dropdown-menu-trigger>
          <ui-dropdown-menu-content align="end" class="min-w-44">
            <ui-dropdown-menu-item variant="destructive" @select="() => deleteBrand()">
              <trash-2-icon />
              {{ tr('Delete') }}
            </ui-dropdown-menu-item>
          </ui-dropdown-menu-content>
        </ui-dropdown-menu>
      </template>
    </page-header>

    <div class="flex flex-col gap-5 py-5">
      <asset-status-message
        :brand="brand"
        :ad-account="brand?.meta_ad_account"
        :source-profiles="brand?.sourceProfiles ?? []"
      />

      <div class="grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <sticky-container :active="$root.screenWidth >= 1024" class="flex flex-col gap-5">
          <section class="glass-panel flex flex-col gap-4 rounded-xl p-5">
            <header class="flex items-center justify-between gap-3">
              <h2 class="m-0 flex items-center gap-2 text-sm font-semibold text-foreground">
                <span :class="[ 'size-2 rounded-full', brand && !brand.isInactive ? 'bg-success' : 'bg-muted-foreground/50' ]" />
                {{ tr('Brand') }}:
                <ui-skeleton v-if="isLoading" class="h-4 w-14" />
                <template v-else>{{ brand.isInactive ? tr('brand.inactive') : tr('Active') }}</template>
              </h2>
              <switcher v-if="isLoading" disabled />
              <switcher
                v-else
                v-tooltip="brandSwitchTooltip(brand)"
                :model-value="!brand.isInactive"
                :disabled="isBrandSwitchDisabled(brand)"
                :loading="isBrandStatusLoading(brand)"
                :aria-label="tr('Active')"
                @update:model-value="(val) => onChangeBrandStatus(brand, val)"
              />
            </header>

            <div v-if="isLoading" class="flex flex-col gap-3">
              <ui-skeleton class="h-4 w-2/5" />
              <ui-skeleton class="h-4 w-1/5" />
            </div>
            <dl v-else class="m-0 flex flex-col divide-y divide-border text-sm">
              <div class="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <dt class="shrink-0 text-muted-foreground">{{ tr('Created') }}</dt>
                <dd class="m-0 min-w-0 text-right font-medium text-foreground">
                  <span v-if="brand.created_at" v-tooltip="formatDateWithTime(brand.created_at)">{{ timeAgo(brand.created_at, tr) }}</span>
                  <template v-else>–</template>
                </dd>
              </div>
              <div v-if="brand.isInactive && brand.deactivated_at" class="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <dt class="shrink-0 text-muted-foreground">{{ tr('Inactive since') }}</dt>
                <dd class="m-0 min-w-0 text-right font-medium text-foreground">
                  <span v-tooltip="formatDateWithTime(new Date(brand.deactivated_at))">{{ timeAgo(new Date(brand.deactivated_at), tr) }}</span>
                </dd>
              </div>
              <div class="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <dt class="shrink-0 text-muted-foreground">{{ tr('Automations') }}</dt>
                <dd class="m-0 min-w-0 text-right font-medium text-foreground">{{ brand.rules.length }}</dd>
              </div>
            </dl>
          </section>

          <section class="glass-panel flex flex-col gap-4 rounded-xl p-5">
            <header class="flex items-center justify-between gap-3">
              <h2 class="m-0 text-sm font-semibold text-foreground">
                {{ tr('Meta Platform') }}
              </h2>
              <i class="icon meta size-4.5 shrink-0 bg-muted-foreground" />
            </header>

            <div v-if="isLoading" class="flex flex-col gap-3">
              <ui-skeleton class="h-9 w-3/5 rounded-full" />
              <ui-skeleton class="h-4 w-2/5" />
              <ui-skeleton class="h-4 w-3/5" />
            </div>
            <template v-else>
              <div v-if="brand.sourceProfiles.length" class="flex flex-wrap gap-x-6 gap-y-2">
                <social-page
                  v-for="profile in brand.sourceProfiles"
                  :key="'source-profile--' + profile.id"
                  :source-profile="profile"
                />
              </div>

              <dl class="m-0 flex flex-col divide-y divide-border text-sm">
                <div v-if="brand.website" class="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <dt class="shrink-0 text-muted-foreground">{{ tr('Website') }}</dt>
                  <dd class="m-0 min-w-0 text-right font-medium text-foreground truncate">
                    <a :href="brand.website" target="_blank" class="text-primary hover:underline">{{ brand.website }}</a>
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <dt class="shrink-0 text-muted-foreground">{{ tr('Ad Account') }}</dt>
                  <dd class="m-0 min-w-0 text-right font-medium text-foreground">{{ brand.meta_ad_account ? brand.meta_ad_account.name : '–' }}</dd>
                </div>
                <div class="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <dt class="shrink-0 text-muted-foreground">{{ tr('Currency') }}</dt>
                  <dd class="m-0 min-w-0 text-right font-medium text-foreground">{{ brand.meta_ad_account ? brand.meta_ad_account.currency : '–' }}</dd>
                </div>
                <div class="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <dt class="shrink-0 text-muted-foreground">{{ tr('Spend Cap') }}</dt>
                  <dd :class="[ 'm-0 min-w-0 text-right font-medium text-foreground', 'flex items-center justify-end gap-1.5' ]">
                    <ui-button
                      v-if="brand.meta_ad_account && canEditSpendCap"
                      v-tooltip="tr('Edit spend cap')"
                      variant="ghost"
                      size="icon"
                      class="size-6 text-primary"
                      :aria-label="tr('Edit spend cap')"
                      @click="() => openSpendCapModal()"
                    >
                      <pencil-icon class="size-3.5" />
                    </ui-button>
                    <template v-if="brand.meta_ad_account?.spend_cap > 0">
                      {{ formatPrice(brand.meta_ad_account.spend_cap, brand.meta_ad_account.currencySymbol) }}
                    </template>
                    <span v-else class="font-normal text-muted-foreground italic">{{ tr('No spend cap set') }}</span>
                  </dd>
                </div>
              </dl>

              <div v-if="$root.isSupportMode && $root.supportHasL2Access" class="flex flex-wrap gap-2">
                <ui-button
                  v-if="brand.meta_ad_account"
                  variant="outline"
                  size="xs"
                  @click="() => openCommunicatorDebug(brand.meta_ad_account)"
                >
                  <bug-icon />
                  {{ tr('Ad Account') }}
                </ui-button>
                <ui-button
                  v-if="brand.facebook_source_profile"
                  variant="outline"
                  size="xs"
                  @click="() => openCommunicatorDebug(brand.facebook_source_profile)"
                >
                  <bug-icon />
                  {{ tr('Facebook Page') }}
                </ui-button>
                <ui-button
                  v-if="brand.instagram_source_profile"
                  variant="outline"
                  size="xs"
                  @click="() => openCommunicatorDebug(brand.instagram_source_profile)"
                >
                  <bug-icon />
                  {{ tr('Instagram Profile') }}
                </ui-button>
              </div>
            </template>
          </section>

          <rule-links-card
            :title="tr('Automations')"
            :rules="brand?.rules ?? []"
            :loading="isLoading"
            :empty-text="tr('No automations linked to this brand.')"
          />
        </sticky-container>

        <content-panel :title="tr('Activities')" :icon="HistoryIcon" class="min-w-0">
          <activity-log :brand-id="$route.params.id" />
        </content-panel>
      </div>
    </div>
  </div>
</template>

<script>
import { Bug, Ellipsis, History, Pencil, Trash2 } from '@lucide/vue';
import { markRaw } from 'vue';

import BrandActivationMixin from '@/mixins/BrandActivation.js';
import BrandService from '@/services/Brand.js';
import ActivityLog from '@/ui/components/ActivityLog.vue';
import AdAccountSpendCapModalContent from '@/ui/components/AdAccountSpendCapModalContent.vue';
import AssetStatusMessage from '@/ui/components/AssetStatusMessage.vue';
import CommunicatorDebug from '@/ui/components/CommunicatorDebug.vue';
import ContentPanel from '@/ui/components/ContentPanel.vue';
import DisconnectConfirmModal from '@/ui/components/DisconnectConfirmModal.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import RuleLinksCard from '@/ui/components/RuleLinksCard.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import StickyContainer from '@/ui/components/StickyContainer.vue';
import Switcher from '@/ui/components/Switcher.vue';
import { formatTime, formatToYMD, timeAgo } from '@/utils/date.js';
import { formatPrice } from '@/utils/number.js';

export default {
  name: 'BrandOverviewView',
  components: {
    ActivityLog,
    AssetStatusMessage,
    BugIcon: Bug,
    ContentPanel,
    EllipsisIcon: Ellipsis,
    PencilIcon: Pencil,
    RuleLinksCard,
    SocialPage,
    StickyContainer,
    PageHeader,
    Switcher,
    Trash2Icon: Trash2,
  },
  mixins: [ BrandActivationMixin ],
  data: () => ({
    isLoading: true,
    brand: null,
    HistoryIcon: markRaw(History),
  }),
  computed: {
    canEditSpendCap() {
      return !this.$root.loggedInUser.isRestrictedViewer;
    },
  },
  created() {
    this.loadBrand();
  },
  methods: {
    formatPrice,
    timeAgo,
    formatDateWithTime(date) {
      return formatToYMD(date) + '. ' + formatTime(date);
    },
    onBrandStatusChanged(brand) {
      this.brand = brand;
    },
    openCommunicatorDebug(adAccount) {
      this.$fullscreenModal.open(
        this,
        CommunicatorDebug,
        { entity: adAccount },
        { wide: true },
      );
    },
    loadBrand() {
      this.isLoading = true;
      BrandService.get(this.$route.params.id)
        .then(brand => {
          this.brand = brand;
          return brand;
        })
        .then(() => {
          if (this.$route.query['modal'] === 'spend-cap') {
            this.openSpendCapModal();
          }

          return null;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    deleteBrand() {
      BrandService.deletePrecheck(this.$route.params.id)
        .then(resp => {
          this.$fullscreenModal.open(
            this,
            DisconnectConfirmModal,
            {
              brand: this.brand,
              defuncRules: resp.defunc_rules,
              confirmCallback: () => this.confirmDeleteBrand(),
            },
          );

          return resp;
        })
        .catch(err => this.error(err));
    },
    confirmDeleteBrand() {
      return BrandService.delete(this.$route.params.id)
        .then(async () => {
          await this.$store.dispatch('auth/fetch');

          this.$toast.clear();
          this.$toast.success(this.tr('Brand deleted successfully.'));
          this.$router.push('/brands');

          return true;
        })
        .catch(err => this.error(err));
    },
    openSpendCapModal() {
      if (!this.canEditSpendCap) {
        return;
      }

      this.$fullscreenModal.open(
        this,
        AdAccountSpendCapModalContent,
        { adAccount: this.brand.meta_ad_account },
      );
    },
  },
};
</script>
